import {
  type FC,
  Fragment,
  type Ref,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/new-ui/button';
import { TableRow } from '@/components/ui/table';
import { getProjectEstates, type ProjectEstates } from '@/features/projects/api/project-estates';
import { useInfiniteScroll } from '@/hooks/use-infinite-scroll';
import type { SortByValues } from '@/utils/sortTypes';

interface EstatesInfiniteScrollProps {
  projectId: string;
  filters?: object;
  sort?: {
    by?: SortByValues;
    order?: 'asc' | 'desc';
  };
  navigateToEstatePage: (estateId: string) => void;
  rowRenderer: (estate: ProjectEstates[number]) => JSX.Element;
  /**
   * 'table-row' expects rowRenderer to return a complete <tr> and renders a valid sentinel row.
   * 'grid' expects rowRenderer to return a card and renders a block sentinel inside a CSS grid.
   * The default keeps the original output.
   */
  variant?: 'default' | 'table-row' | 'grid';
  /** Number of columns, used by the sentinel row of the 'table-row' variant. */
  colSpan?: number;
}

interface EstatesState {
  [key: string]: ProjectEstates[number];
}

export const ProjectEstatesInfiniteScroll: FC<EstatesInfiniteScrollProps> = ({
  filters,
  projectId,
  sort,
  rowRenderer,
  navigateToEstatePage,
  variant = 'default',
  colSpan = 1,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [stopLoad, setStopLoad] = useState(false);
  const [estates, setEstates] = useState<EstatesState | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const querySignature = useMemo(
    () => JSON.stringify({ filters, projectId, sort }),
    [filters, projectId, sort]
  );
  const activeQuerySignatureRef = useRef(querySignature);

  const { lastElementRef, pageNum, resetPageNum } = useInfiniteScroll<HTMLTableRowElement>({
    loading,
    stopLoad,
    startPage: 1,
  });

  const loadMoreEstates = useCallback(async () => {
    if (pageNum <= 1) return;
    if (activeQuerySignatureRef.current !== querySignature) return;

    setLoading(true);
    try {
      setError(null);
      const { estates } = await getProjectEstates({
        projectId,
        query: {
          page: pageNum,
          ...(filters && Object.keys(filters).length && { filters }),
          ...(sort?.by &&
            sort.order && {
              sort: {
                by: sort.by,
                order: sort.order,
              },
            }),
        },
      });
      const estatesPage = estates.data;
      if (activeQuerySignatureRef.current !== querySignature) return;
      if (!estatesPage.length) return setStopLoad(true);

      const estatesCollection = estatesPage.reduce<Record<string, ProjectEstates[number]>>(
        (acc, v) => {
          acc[v.id] = v;
          return acc;
        },
        {}
      );
      setEstates((prev) =>
        prev
          ? {
              ...prev,
              ...estatesCollection,
            }
          : estatesCollection
      );
    } catch (err) {
      // A request for filters or a sort that changed meanwhile is stale, its error does not count
      if (activeQuerySignatureRef.current !== querySignature) return;
      // Stops on the failed page instead of skipping it, the user retries it with the button
      setError(err as Error);
      setStopLoad(true);
    } finally {
      setLoading(false);
    }
  }, [projectId, filters, pageNum, querySignature, sort]);

  useEffect(() => {
    loadMoreEstates();
  }, [loadMoreEstates]);

  useEffect(() => {
    activeQuerySignatureRef.current = querySignature;
    setEstates(null);
    setError(null);
    resetPageNum();
    setStopLoad(false);
  }, [querySignature, resetPageNum]);

  const retry = () => {
    setStopLoad(false);
    void loadMoreEstates();
  };

  // the table and grid variants live inside the new design scope, so they use the new button
  const retryButton =
    error && stopLoad ? (
      <Button type="button" disabled={loading} onClick={retry}>
        {t('tryAgain')}
      </Button>
    ) : null;

  if (variant === 'table-row') {
    return (
      <Fragment>
        {estates
          ? Object.values(estates).map((estate) => (
              <Fragment key={estate.id}>{rowRenderer(estate)}</Fragment>
            ))
          : null}
        {retryButton ? (
          <tr>
            <td colSpan={colSpan} className="p-4 text-center">
              {retryButton}
            </td>
          </tr>
        ) : null}
        {!stopLoad ? (
          <tr ref={lastElementRef}>
            <td colSpan={colSpan} className="p-0" />
          </tr>
        ) : null}
      </Fragment>
    );
  }

  if (variant === 'grid') {
    return (
      <Fragment>
        {estates
          ? Object.values(estates).map((estate) => (
              <Fragment key={estate.id}>{rowRenderer(estate)}</Fragment>
            ))
          : null}
        {retryButton ? (
          <div className="col-span-full flex justify-center py-4">{retryButton}</div>
        ) : null}
        {!stopLoad ? (
          <div
            ref={lastElementRef as unknown as Ref<HTMLDivElement>}
            className="col-span-full h-4"
          />
        ) : null}
      </Fragment>
    );
  }

  return (
    <Fragment>
      {estates
        ? Object.values(estates).map((estate) => {
            return (
              <TableRow
                key={estate.id}
                className="cursor-pointer items-center text-primary-dark"
                onClick={() => navigateToEstatePage(estate.id)}
              >
                {rowRenderer(estate)}
              </TableRow>
            );
          })
        : null}
      {!stopLoad ? <TableRow ref={lastElementRef} className="h-4 p-0" /> : null}
    </Fragment>
  );
};
